import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Save } from "lucide-react";

const FIELDS = {
  store: [
    { key: "store_name", label: "Store name" },
    { key: "phone", label: "Phone number" },
    { key: "email", label: "Contact email", type: "email" },
    { key: "address_line1", label: "Address line 1" },
    { key: "address_line2", label: "Address line 2" },
    { key: "hours_weekday", label: "Weekday hours" },
    { key: "hours_weekend", label: "Weekend hours" },
  ],
  notifications: [
    { key: "admin_notification_email", label: "Order notification inbox (BCC on every order email)", type: "email", full: true },
  ],
  bank: [
    { key: "bank_account_name", label: "Account name" },
    { key: "bank_sort_code", label: "Sort code" },
    { key: "bank_account_number", label: "Account number" },
    { key: "bank_reference_prefix", label: "Reference prefix (e.g. GS)" },
  ],
  banner: [
    { key: "top_banner_text", label: "Top strip banner text", full: true },
  ],
};

export default function AdminSettings({ admin }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // /api/settings is public — plain axios is fine.
        const { data } = await axios.get(`${API}/settings`);
        setForm(data);
      } catch (e) {
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const change = (key) => (e) => {
    const value = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };
  const toggle = (key) => (value) => setForm((f) => ({ ...f, [key]: !!value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await admin.put("/settings", form);
      setForm(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return <div className="text-slate-500">Loading settings…</div>;
  }

  const renderField = ({ key, label, type = "text", full = false }) => (
    <div key={key} className={full ? "col-span-2" : ""}>
      <Label htmlFor={`setting-${key}`}>{label}</Label>
      <Input
        id={`setting-${key}`}
        type={type}
        value={form[key] ?? ""}
        onChange={change(key)}
        data-testid={`setting-${key}`}
      />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Store info */}
      <Card>
        <CardHeader>
          <CardTitle>Store Information</CardTitle>
          <CardDescription>Shown in the site header, footer, contact section and order emails.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.store.map(renderField)}
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
          <CardDescription>Get a copy of every customer order in your inbox so nothing slips past you.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.notifications.map(renderField)}
        </CardContent>
      </Card>

      {/* Payment methods */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Methods</CardTitle>
          <CardDescription>Turn a checkout method off and customers won&apos;t see it. Existing orders are unaffected.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[
            { key: "stripe_enabled", label: "Card payments (Stripe)" },
            { key: "bank_transfer_enabled", label: "Bank transfer" },
            { key: "pay_at_pickup_enabled", label: "Pay at pickup / on delivery" },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between border rounded-lg px-4 py-3">
              <span className="font-medium text-slate-800">{label}</span>
              <Switch
                checked={!!form[key]}
                onCheckedChange={toggle(key)}
                data-testid={`setting-${key}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Bank details */}
      <Card>
        <CardHeader>
          <CardTitle>Bank Transfer Details</CardTitle>
          <CardDescription>Sent to customers who select bank transfer at checkout — via email and SMS.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.bank.map(renderField)}
        </CardContent>
      </Card>

      {/* Delivery */}
      <Card>
        <CardHeader>
          <CardTitle>Saturday Delivery</CardTitle>
          <CardDescription>Manchester (M-postcode) Saturday delivery rules.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-2 flex items-center justify-between border rounded-lg px-4 py-3">
            <span className="font-medium text-slate-800">Saturday delivery available</span>
            <Switch
              checked={!!form.saturday_delivery_enabled}
              onCheckedChange={toggle("saturday_delivery_enabled")}
              data-testid="setting-saturday_delivery_enabled"
            />
          </div>
          <div>
            <Label htmlFor="setting-delivery_min_order">Minimum order value (£)</Label>
            <Input
              id="setting-delivery_min_order"
              type="number"
              step="0.01"
              value={form.delivery_min_order ?? ""}
              onChange={change("delivery_min_order")}
              data-testid="setting-delivery_min_order"
            />
          </div>
        </CardContent>
      </Card>

      {/* Banner */}
      <Card>
        <CardHeader>
          <CardTitle>Top Strip Banner</CardTitle>
          <CardDescription>The thin promotional strip that sits above the header.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-2 flex items-center justify-between border rounded-lg px-4 py-3">
            <span className="font-medium text-slate-800">Show banner</span>
            <Switch
              checked={!!form.top_banner_enabled}
              onCheckedChange={toggle("top_banner_enabled")}
              data-testid="setting-top_banner_enabled"
            />
          </div>
          {FIELDS.banner.map(renderField)}
        </CardContent>
      </Card>

      <div className="flex justify-end sticky bottom-4 z-10">
        <Button
          onClick={save}
          disabled={saving}
          size="lg"
          className="bg-emerald-600 hover:bg-emerald-700 shadow-lg"
          data-testid="save-settings-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving…" : "Save All Settings"}
        </Button>
      </div>
    </div>
  );
}
